import { createUseStyles } from 'react-jss';

/**
 * This card is a fixed height so that lists of cards can be virtualized without
 * measuring each row. Consumers that virtualize (see the active list) derive
 * their row height from these values. Note that changing the card to a variable height
 * would break their layout math.
 */
export const cardHeight = 188;
export const cardMargin = 11;

export const useStyles = createUseStyles({
  slider: {
    margin: `${cardMargin}px`,
    width: 'auto',
    backgroundColor: 'white',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
    boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.04)',
    overflow: 'hidden',
    cursor: 'pointer',
    // Animation styles
    transition:
      'box-shadow 0.2s ease, transform 0.1s ease, background-color 0.3s ease, border-color 0.3s ease',
    '&:hover': {
      boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0px)',
    },
    height: `${cardHeight}px`,

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.3)',

      '&:hover': {
        boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.5)',
        transform: 'translateY(-2px)',
      },
    },
  },
  item: {
    height: '100%',
    alignItems: 'stretch',
    '--min-height': '100%',
    '--padding-start': '0px',
    '--padding-end': '0px',
    '--inner-padding-end': '0px',
    '--inner-padding-start': '0px',
    '--background': 'transparent',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    alignSelf: 'stretch',
    color: 'inherit',
    textDecoration: 'none',
    backgroundColor: 'white',
    transition: 'background-color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: '#1a1a1a',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 14px 0',
  },
  status: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flex: 'none',
    backgroundColor: (props: any) => `${props.statusColor}14`,
    borderRadius: '6px',
    padding: '5px 9px',
    fontSize: '12.5px',
    fontWeight: '600',
    lineHeight: '1',
    color: (props: any) => props.statusColor,
    transition: 'background-color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: (props: any) => `${props.statusColor}30`,
    },
  },
  category: {
    flex: 1,
    minWidth: 0,
    fontSize: '12.5px',
    fontWeight: '500',
    color: '#8a8f98',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  content: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '9px 14px 13px',
    gap: '8px',
  },
  body: {
    minWidth: 0,
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    'h1&': {
      margin: 0,
      padding: 0,
      fontFamily: 'inherit',
    },
    fontSize: '19px',
    fontWeight: '700',
    lineHeight: '1.25',
    color: '#282828',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#ffffff',
    },
  },
  location: {
    'p&': {
      margin: '3px 0 0',
      padding: 0,
      fontFamily: 'inherit',
    },
    fontSize: '14px',
    fontWeight: '500',
    color: '#92949c',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  statusCallout: {
    margin: 'auto 0 0',
    background: 'linear-gradient(to right, rgba(120, 147, 172, 0.115), transparent)',
    borderLeft: '2px solid #7893AC',
    padding: '4px 10px',
    transition: 'background 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      background: 'linear-gradient(to right, rgba(120, 147, 172, 0.25), transparent)',
    },
  },
  calloutValue: {
    'p&': {
      margin: 0,
      padding: 0,
      fontFamily: 'inherit',
    },
    fontSize: '13.5px',
    fontStyle: 'italic',
    lineHeight: '1.45',
    color: '#666666',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    transition: 'color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
    },
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#7c8088',
    margin: '0 -14px',
    padding: '10px 14px 0',
    borderTop: '1px solid #EAEAEA',
    transition: 'color 0.3s ease, border-color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      color: '#b3b3b3',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    },
  },
  assignee: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  createdDate: {
    flex: 'none',
  },
  footerRightGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: 'auto',
  },
  comments: {
    flex: 'none',
  },
  resident: {
    display: 'flex',
    alignItems: 'center',
    flex: 'none',
    backgroundColor: 'rgba(71, 120, 118, 0.1)',
    borderRadius: '6px',
    padding: '5px 9px',
    fontSize: '12.5px',
    fontWeight: '600',
    lineHeight: '1',
    color: 'var(--ion-color-primary)',
    transition: 'background-color 0.3s ease',

    // Dark mode styles
    '@media (prefers-color-scheme: dark)': {
      backgroundColor: 'rgba(71, 120, 118, 0.25)',
    },
  },
});
